import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

const JSON_HEADERS: Record<string, string> = { 'Content-Type': 'application/json' };

/**
 * Builds the Authorization header for the backends that authenticate a **Firebase ID token**
 * rather than the Welby JWT — today markus-call-processor, reached via
 * `environment.call_transcriptions_endpoint`.
 *
 * Two token types are in play and they are not interchangeable: welby-micro-service issues and
 * verifies its own HS-signed JWT (attached by `AuthInterceptor`), while markus-call-processor
 * verifies with Firebase Admin `verifyIdToken()`. Sending the Welby JWT there is a guaranteed 401,
 * which is how answering an inbound call ended up signing care managers out.
 *
 * Lives in one place so every caller of that service uses the same token; `AuthInterceptor` is
 * deliberately scoped away from those URLs so it cannot overwrite this header.
 */
@Injectable({ providedIn: 'root' })
export class FirebaseIdTokenService {
  constructor(private afAuth: AngularFireAuth) {}

  /**
   * Headers carrying the current user's Firebase ID token.
   *
   * Reads `afAuth.idToken`, NOT `afAuth.currentUser`. `currentUser` goes through the compat lazy
   * SDK proxy, which resolves as soon as `app.auth()` is constructed — before
   * `onAuthStateChanged` has restored the persisted session — so a call made during boot would see
   * `null` and silently send no credential. `idToken` is built on `onIdTokenChanged`
   * (`@angular/fire` compat auth), so its first emission is post-restore, and it mints through
   * `getIdToken()`, which refreshes an expired token.
   *
   * No signed-in user emits the plain JSON headers: the request then gets the backend's own 401,
   * which is the honest answer and what the callers already handle. A failure to mint a token for a
   * user who IS signed in propagates instead of being flattened into the same shape — the two are
   * different problems and only the caller knows whether one is fatal.
   */
  authHeaders(): Observable<Record<string, string>> {
    return this.afAuth.idToken.pipe(
      take(1),
      map((token) => (token ? { ...JSON_HEADERS, Authorization: `Bearer ${token}` } : { ...JSON_HEADERS }))
    );
  }
}
